import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Save, Loader2, ShieldCheck, CalendarClock } from "lucide-react";
import { runOrQueue } from "@/lib/offlineQueue";
import { PlzCityInput, StreetAutocomplete } from "@/components/PlzCityInput";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AutofillInput from "@/components/AutofillInput";

const inputCls = "h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none";
const labelCls = "block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider";

const CUSTOMER_TYPES = ["Eigentümer","Mieter","Verwalter"];

export default function CustomerSection({ project, customer, onUpdate }) {
  const [form, setForm] = useState({ ...customer });
  const [loading, setLoading] = useState(false);
  const [realToggling, setRealToggling] = useState(false);
  const [installDate, setInstallDate] = useState(project?.desired_installation_date || "");
  const [installSaving, setInstallSaving] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canTogglePrivileged = ["sales_director", "admin"].includes(user?.role);

  // Iter89y (2026-02-25) — Funktionales setState statt `{...form, [k]: v}`
  // als Spread über eine Closure. Grund: schnelles Hintereinander-Ändern
  // mehrerer Felder (z.B. Anrede wählen → sofort Vorname tippen) führte
  // bei der alten Variante zu einem Stale-Closure, bei dem der zweite
  // Setter den eben erst gesetzten Wert des ersten überschrieb — die
  // Anrede „sprang" auf leer zurück. Mit `setForm((prev) => ...)` wird
  // immer der neueste State verwendet.
  const upd = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Iter88zz6 — Rep-Liste für das Sachbearbeiter-Dropdown (provisions-relevant).
  const [reps, setReps] = useState([]);
  const [savingRep, setSavingRep] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const canAssignAny = ["admin", "super_admin", "sales_director", "sales_manager", "branch_manager"].includes(user?.role);
  const canSelfAssign = user?.role === "sales_rep";
  useEffect(() => {
    // Fetch alle sichtbaren User im Mandanten und filter clientseitig
    // auf provisions-relevante Rollen (Backend hat keinen include_roles).
    (async () => {
      try {
        const r = await api.get("/users");
        const all = Array.isArray(r.data) ? r.data : (r.data?.users || []);
        const relevant = ["sales_rep", "sales_manager", "branch_manager", "sales_director"];
        setReps(all.filter((u) => relevant.includes(u.role) && u.status !== "deleted"));
      } catch { /* keep empty */ }
    })();
  }, []);
  const saveAssignedRep = async (newId) => {
    if (!customer?.id) return;
    setSavingRep(true);
    try {
      await api.patch(`/customers/${customer.id}/assigned-rep`, { assigned_rep_user_id: newId || null });
      setForm((f) => ({ ...f, assigned_rep_user_id: newId || null }));
      toast.success(newId ? "Vertriebler zugewiesen" : "Zuweisung entfernt");
      onUpdate?.();
      if (showHistory) loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
    } finally {
      setSavingRep(false);
    }
  };

  const loadHistory = async () => {
    if (!customer?.id) return;
    try {
      const r = await api.get(`/customers/${customer.id}/assigned-rep/history?limit=20`);
      setHistory(r.data?.entries || []);
    } catch { setHistory([]); }
  };
  const toggleHistory = () => {
    if (!showHistory) loadHistory();
    setShowHistory((v) => !v);
  };

  const saveInstallDate = async () => {
    if (!project?.id) return;
    setInstallSaving(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        desired_installation_date: installDate || null,
      });
      toast.success(
        installDate
          ? `Umsetzungstermin gespeichert: ${new Date(installDate).toLocaleDateString("de-DE")}`
          : "Umsetzungstermin entfernt",
      );
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setInstallSaving(false);
    }
  };

  const toggleRealWorkflow = async () => {
    if (!project?.id || !canTogglePrivileged || realToggling) return;
    const next = !(project.is_real_workflow || form.is_real_customer);
    setRealToggling(true);
    try {
      await api.post(`/projects/${project.id}/mark-real-workflow`, { is_real_workflow: next });
      setForm((prev) => ({ ...prev, is_real_customer: next }));
      toast.success(next
        ? "Als echten Vorgang markiert — vor Test-Reset geschützt"
        : "Echter-Vorgang-Flag entfernt");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally {
      setRealToggling(false);
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || "",
        phone: form.phone || "",
        street: form.street,
        house_number: form.house_number,
        zip: form.zip,
        city: form.city,
        customer_type: form.customer_type || "Eigentümer",
        salutation: form.salutation || "",
        sachbearbeiter_name: form.sachbearbeiter_name || "",
        sachbearbeiter_email: form.sachbearbeiter_email || "",
        // Iter88zbj — B2B-Klassifizierung + Firmenname + USt-ID
        // (relevant für PV-Angebots-PDF Adressblock).
        is_business: !!form.is_business,
        company_name: (form.company_name || "").trim(),
        vat_id: (form.vat_id || "").trim().toUpperCase(),
      };
      const r = await runOrQueue({
        method: "patch",
        url: `/projects/${project.id}/customer`,
        data: payload,
        label: `Kundendaten: ${form.last_name}`,
      });
      if (r.offline) {
        toast.success("Offline – Kundendaten werden synchronisiert");
      } else {
        toast.success("Kundendaten gespeichert — weiter zu Verbrauch");
        onUpdate?.();
        // Auto-Next: nach dem Speichern der Kundendaten direkt zum Verbrauch-Tab
        // springen (das ist der nächste logische Schritt). Verhindert, dass der
        // User auf einer alten „letzten Tab"-Position landet (localStorage-Memory).
        const next = new URLSearchParams(searchParams);
        next.set("tab", "consumption");
        navigate(`/projects/${project.id}?${next.toString()}`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold text-white">Kunde</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {(project?.is_real_workflow || form.is_real_customer) && (
              <span
                data-testid="real-workflow-badge"
                title="Vor Test-Daten-Reset geschützt"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 uppercase tracking-wider"
              >
                <ShieldCheck size={11} /> Echt
              </span>
            )}
            {form.customer_number && (
              <div
                data-testid="cust-number-badge"
                className="text-[12px] font-mono bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] rounded-lg px-2.5 py-1 inline-flex items-center gap-1.5"
                title="Fortlaufende Kundennummer · automatisch vergeben"
              >
                Kunden-Nr.&nbsp;<span className="font-bold tracking-wider text-white">{form.customer_number}</span>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Anrede</label>
            <select
              data-testid="cust-salutation"
              className={inputCls}
              value={form.salutation || ""}
              onChange={(e) => upd("salutation", e.target.value)}
            >
              <option value="">(keine Anrede)</option>
              <option value="male">Herr</option>
              <option value="female">Frau</option>
              <option value="family">Familie</option>
              <option value="diverse">Divers</option>
            </select>
          </div>
          <div /> {/* spacer to keep grid balanced */}
          <div><label className={labelCls}>Vorname</label><input data-testid="cust-firstname" className={inputCls} value={form.first_name || ""} onChange={(e) => upd("first_name", e.target.value)} /></div>
          <div><label className={labelCls}>Nachname</label><input data-testid="cust-lastname" className={inputCls} value={form.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} /></div>
          <div><label className={labelCls}>Telefon</label>
            <AutofillInput
              dataTestid="cust-phone"
              className={inputCls}
              storageKey="customer_phone"
              type="tel"
              autoComplete="tel"
              value={form.phone || ""}
              onChange={(v) => upd("phone", v)}
            />
          </div>
          <div><label className={labelCls}>E-Mail</label>
            <AutofillInput
              dataTestid="cust-email"
              className={inputCls}
              storageKey="customer_email"
              type="email"
              autoComplete="email"
              value={form.email || ""}
              onChange={(v) => upd("email", v)}
            />
          </div>
        </div>
        {/* Iter88s (2026-02-19): Sachbearbeiter (Innendienst-Ansprechpartner) —
            erscheint oben rechts im PDF-Angebot und wird beim Versand als CC
            vorbelegt. Freitext-Feld, kein Pflichtfeld. */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800/60">
          <div>
            <label className={labelCls}>Sachbearbeiter (Innendienst)</label>
            <AutofillInput
              dataTestid="cust-sachbearbeiter-name"
              className={inputCls}
              storageKey="sachbearbeiter_name"
              autoComplete="name"
              placeholder="z.B. Frau Müller"
              value={form.sachbearbeiter_name || ""}
              onChange={(v) => upd("sachbearbeiter_name", v)}
            />
          </div>
          <div>
            <label className={labelCls}>Sachbearbeiter-E-Mail (CC-Empfänger)</label>
            <AutofillInput
              dataTestid="cust-sachbearbeiter-email"
              className={inputCls}
              storageKey="sachbearbeiter_email"
              type="email"
              autoComplete="email"
              placeholder="mueller@..."
              value={form.sachbearbeiter_email || ""}
              onChange={(v) => upd("sachbearbeiter_email", v)}
            />
          </div>
        </div>
        {/* Iter88zz6 — Verantwortlicher Vertriebler steuert die
            Angebots-Provision (User → Manager → Direktor → Mandant).
            Getrennt vom Innendienst-Sachbearbeiter oben. */}
        <div className="pt-3 border-t border-zinc-800/60">
          <label className={labelCls}>Verantwortlicher Vertriebler <span className="text-amber-300">*</span> <span className="text-zinc-400 normal-case tracking-normal font-normal">— steuert Provision & Endkundenpreise</span></label>
          {canAssignAny || canSelfAssign ? (
            <select
              data-testid="cust-assigned-rep"
              className={inputCls}
              value={form.assigned_rep_user_id || ""}
              disabled={savingRep}
              onChange={(e) => saveAssignedRep(e.target.value || null)}
            >
              <option value="">— nicht zugewiesen —</option>
              {canSelfAssign && !canAssignAny && (
                <option value={user?.id}>
                  {user?.first_name} {user?.last_name} (ich)
                </option>
              )}
              {canAssignAny && reps.map((r) => {
                const roleLabel = r.role === "sales_rep" ? "Vertriebler"
                  : r.role === "sales_manager" ? "Vertriebsleiter"
                  : r.role === "branch_manager" ? "Niederlassungsleiter"
                  : r.role === "sales_director" ? "Vertriebsdirektor" : r.role;
                return (
                  <option key={r.id} value={r.id}>{`${r.first_name || ""} ${r.last_name || ""} · ${roleLabel}`.trim()}</option>
                );
              })}
            </select>
          ) : (
            <div className="text-[13px] text-zinc-300">
              {form.assigned_rep_user_id
                ? (reps.find((r) => r.id === form.assigned_rep_user_id)?.first_name || "") + " " + (reps.find((r) => r.id === form.assigned_rep_user_id)?.last_name || "")
                : <span className="text-amber-300">Noch nicht zugewiesen — Vertriebsleiter kontaktieren.</span>}
            </div>
          )}
          {!form.assigned_rep_user_id && (
            <p className="text-[11px] text-amber-300 mt-1.5" data-testid="cust-assigned-rep-warning">
              ⚠ Ohne zugewiesenen Vertriebler können keine Angebote erstellt werden (Preise nicht berechenbar).
            </p>
          )}
          {/* Iter88zz7 — Historie der Sachbearbeiter-Zuweisungen */}
          <button
            type="button"
            data-testid="cust-assigned-rep-history-toggle"
            onClick={toggleHistory}
            className="mt-2 text-[11px] text-zinc-400 hover:text-zinc-200 underline underline-offset-2"
          >
            {showHistory ? "Historie ausblenden" : "Zuweisungs-Historie ansehen"}
          </button>
          {showHistory && (
            <div className="mt-2 rounded-lg border border-zinc-800 bg-[#0E0E12] p-3 space-y-1.5" data-testid="cust-assigned-rep-history">
              {history.length === 0 ? (
                <div className="text-[12px] text-zinc-500">Noch keine Zuweisungs-Änderungen.</div>
              ) : history.map((h) => {
                const when = h.ts ? new Date(h.ts).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
                const oldL = h.old_value_label || (h.old_value ? h.old_value.slice(0, 8) : "—");
                const newL = h.new_value_label || (h.new_value ? h.new_value.slice(0, 8) : "—");
                return (
                  <div key={h.id} className="text-[12px] text-zinc-300 leading-relaxed">
                    <span className="font-mono text-zinc-500">{when}</span>
                    {" · "}
                    <span className="text-zinc-100 font-semibold">{h.actor_name || "System"}</span>
                    {" (" + (h.actor_role || "—") + "): "}
                    <span className="text-zinc-400">{oldL}</span>
                    {" → "}
                    <span className="text-[#00E575] font-semibold">{newL}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
        <h2 className="font-display text-xl font-bold text-white">Adresse</h2>
        {/* Iteration 85 (2026-02-16): PLZ+Ort ZUERST — die PLZ füllt den Ort
            automatisch, und daraus leiten wir Straßen-Vorschläge ab. */}
        <div className="grid grid-cols-3 gap-3">
          <PlzCityInput
            zip={form.zip}
            city={form.city}
            onZipChange={(v) => upd("zip", v)}
            onCityChange={(v) => upd("city", v)}
            zipTestId="cust-zip"
            cityTestId="cust-city"
          />
          <div className="col-span-2">
            <StreetAutocomplete
              zip={form.zip}
              street={form.street}
              onStreetChange={(v) => upd("street", v)}
              testid="cust-street"
            />
          </div>
          <div><label className={labelCls}>Nr.</label><input data-testid="cust-housenum" className={inputCls} value={form.house_number || ""} onChange={(e) => upd("house_number", e.target.value)} /></div>
        </div>
        <div>
          <label className={labelCls}>Eigentümerstatus</label>
          <select data-testid="cust-type" className={inputCls} value={form.customer_type || "Eigentümer"} onChange={(e) => upd("customer_type", e.target.value)}>
            {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Iter88zbj (2026-02-19) — B2B-Rechnungsdaten. Firmenname & USt-ID
          werden im PV-Angebots-PDF im Adressblock ausgegeben, sobald
          „Gewerblich" ausgewählt ist. */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
        <h2 className="font-display text-xl font-bold text-white">Kundentyp & Rechnungsdaten</h2>
        <div>
          <label className={labelCls}>Umsatzsteuer-Klassifizierung</label>
          <select
            data-testid="cust-is-business"
            className={inputCls}
          value={form.is_business ? "business" : "private"}
            onChange={(e) => upd("is_business", e.target.value === "business")}
          >
            <option value="private">Privatperson (0 % USt · § 12 Abs. 3 UStG)</option>
            <option value="business">Gewerblich (19 % Mehrwertsteuer)</option>
          </select>
        </div>
        {form.is_business && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>Firmenname</label>
              <input
                data-testid="cust-company-name"
                className={inputCls}
                placeholder="z. B. Müller GmbH"
                value={form.company_name || ""}
                onChange={(e) => upd("company_name", e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>USt-ID</label>
              <input
                data-testid="cust-vat-id"
                className={inputCls}
                placeholder="z. B. DE123456789"
                value={form.vat_id || ""}
                onChange={(e) => upd("vat_id", e.target.value.toUpperCase())}
                autoCapitalize="characters"
              />
              <p className="text-[12px] text-zinc-400 mt-1.5 leading-snug">
                Wird im PV-Angebots-PDF unter dem Adressblock als
                Referenzzeile ausgewiesen.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Projekt-Eckdaten — z.B. gewünschter Umsetzungstermin (war bisher nur
          beim Anlegen sichtbar). Eigener Save-Button, weil das ein
          Projekt-Feld ist, nicht Teil des Kundendatensatzes. */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
        <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
          <CalendarClock size={20} className="text-[#00E575]" /> Projekt-Eckdaten
        </h2>
        <div>
          <label className={labelCls}>Gewünschter Umsetzungstermin</label>
          <div className="flex gap-2">
            <input
              data-testid="cust-install-date"
              type="date"
              className={inputCls + " flex-1"}
              value={installDate}
              onChange={(e) => setInstallDate(e.target.value)}
            />
            <button
              type="button"
              data-testid="cust-install-date-save"
              onClick={saveInstallDate}
              disabled={installSaving || installDate === (project?.desired_installation_date || "")}
              className="h-12 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500 inline-flex items-center gap-1.5 shrink-0"
            >
              {installSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Speichern
            </button>
          </div>
          <p className="text-[12px] text-zinc-300 mt-1.5 leading-snug">
            Wunschtermin des Kunden für die Inbetriebnahme. Wird im PDF-Angebot ausgewiesen und für die
            Installateur-Disposition verwendet. Leer lassen, wenn noch unbekannt.
          </p>
        </div>
      </div>

      {canTogglePrivileged && (
        <div
          data-testid="real-workflow-toggle-section"
          className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-zinc-100 font-semibold">
              <ShieldCheck size={16} className="text-emerald-400" />
              Echter Vorgang
            </div>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              Wenn aktiviert, bleibt dieser Vorgang von „Test-Daten zurücksetzen&ldquo;
              ausgeschlossen und kann über App-Versionen hinweg für End-to-End-
              Workflow-Tests genutzt werden. Nur Vertriebsleiter & Admin.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleRealWorkflow}
            disabled={realToggling}
            data-testid="real-workflow-toggle-btn"
            className={`px-4 h-11 rounded-xl font-bold text-sm transition shrink-0 ${
              (project?.is_real_workflow || form.is_real_customer)
                ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                : "bg-[#00E575] text-[#09090B] hover:bg-[#00C964]"
            } disabled:opacity-50`}
          >
            {realToggling
              ? "Speichern…"
              : (project?.is_real_workflow || form.is_real_customer)
                ? "Echt-Markierung entfernen"
                : "Als echten Vorgang markieren"}
          </button>
        </div>
      )}

      <button
        data-testid="cust-save-btn"
        onClick={save}
        disabled={loading}
        className="h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition flex items-center justify-center"
      >
        {loading ? <Loader2 className="animate-spin" /> : <><Save size={20} className="mr-2" /> Kundendaten speichern</>}
      </button>
    </div>
  );
}
